import * as React from 'react';
import type { QualityStatus } from '@loci/types';

import { cn } from '@/components/atoms/primitives/utils';
import { Icon } from '@/components/atoms/primitives/icon';
import {
  DETAILS_UNCONFIRMED_DESCRIPTION,
  DETAILS_UNCONFIRMED_LABEL,
  isDetailsUnconfirmed,
} from '@/lib/copy/qualityLabel';

export interface DetailsUnconfirmedBadgeProps {
  /**
   * The event's projected placement status. Optional + nullable so the badge
   * is safe to drop into any surface that may or may not have the field on the
   * wire (the backend only projects it when the operator-gated quality gate is
   * on — see `lib/copy/qualityLabel.ts`).
   */
  readonly qualityStatus: QualityStatus | null | undefined;
  /**
   * Size variant. `inline` is the compact card chip; `block` is the slightly
   * larger detail-page treatment with the full description visible alongside
   * the label.
   */
  readonly variant?: 'inline' | 'block';
  /** Additional CSS classes merged onto the root element. */
  readonly className?: string;
}

/**
 * DetailsUnconfirmedBadge — honest-labeling affordance (WEB-451).
 *
 * Renders a small "Details unconfirmed" badge **only** when the event's
 * `qualityStatus` is `LOW_CONFIDENCE`. For every other value — including
 * `PREMIUM`, `STANDARD`, and the absent/undefined case (quality gate off) —
 * it renders nothing.
 *
 * Accessibility: the badge is never color-only. It pairs a `TriangleAlert`
 * glyph (decorative, `aria-hidden`) with the visible text label, and exposes
 * the longer description via `title` plus an `aria-label` so assistive tech
 * announces the full meaning. Colour is sourced from the theme-aware
 * `--loci-color-warning` design token (no hardcoded palette), and the badge
 * stays legible in both light and dark themes.
 *
 * Pure presentational component — `qualityStatus` is the entire contract.
 */
export function DetailsUnconfirmedBadge({
  qualityStatus,
  variant = 'inline',
  className,
}: DetailsUnconfirmedBadgeProps): React.ReactElement | null {
  if (!isDetailsUnconfirmed(qualityStatus)) {
    return null;
  }

  const isBlock = variant === 'block';

  return (
    <span
      role="note"
      aria-label={`${DETAILS_UNCONFIRMED_LABEL}. ${DETAILS_UNCONFIRMED_DESCRIPTION}.`}
      title={DETAILS_UNCONFIRMED_DESCRIPTION}
      data-testid="details-unconfirmed-badge"
      data-quality-status={qualityStatus}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        'border border-loci-warning/40',
        'bg-loci-warning/12 text-loci-warning',
        isBlock ? 'px-3 py-1 text-small' : 'px-2 py-0.5 text-micro',
        className,
      )}
    >
      <Icon
        name="TriangleAlert"
        aria-hidden="true"
        className={isBlock ? 'size-4 shrink-0' : 'size-3 shrink-0'}
      />
      <span>{DETAILS_UNCONFIRMED_LABEL}</span>
      {isBlock ? (
        <span className="text-loci-text-muted">· {DETAILS_UNCONFIRMED_DESCRIPTION}</span>
      ) : null}
    </span>
  );
}
